/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { not } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import School from '@linc-technologies/linced-common/enums/nz-moe-school';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import DepartureReason from 'linced-app/enums/nz-moe-departure-reason';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import Area from 'linced-app/enums/nz-moe-area';
import PostSchoolActivity from 'linced-app/enums/nz-moe-post-school-activity';
import { JurisdictionalIdValidations } from 'linced-app/utils/fields';
import { validateLeaver } from 'linced-app/utils/nz-moe/leaver';
import moment from 'moment';

const jurisdictionalIdValidator =
  JurisdictionalIdValidations[Jurisdiction.NZ_MOE];

export default class Unenrol extends Component {
  @service
  options;

  @service
  strings;

  DepartureReason = DepartureReason;
  Area = Area;
  PostSchoolActivity = PostSchoolActivity;
  School = School;

  @tracked
  step = 0;

  @action
  setStep(value) {
    this.step = value;
  }

  @optionAlias('nzMOE:schoolType:type')
  schoolType;

  // Disable ENROL arrival/leaver uploads unless specifically enabled
  // for the school, to facilitate approval testing without full release.
  @optionAlias('hero:nzMOE:enrolArrivalsLeaversEnabled')
  nzMoeEnrolArrivalsLeaversEnabled;

  @not('nzMoeEnrolArrivalsLeaversEnabled')
  enrolArrivalsLeaversDisabled;

  @tracked
  endDate = '';

  @action
  setEndDate(value) {
    this.endDate = value;
  }

  @tracked
  reason = findBy(DepartureReason.values, 'value', 'S'); // Transferred to another school in New Zealand

  @action
  setReason(value) {
    this.reason = value;
  }

  @tracked
  destinationSchool = null;

  @action
  setDestinationSchool(value) {
    this.destinationSchool = value;
  }

  @tracked
  destinationArea = null;

  @action
  setDestinationArea(value) {
    this.destinationArea = value;
  }

  @tracked
  learnerFlags = null;

  editing = false;

  allFlags = Object.freeze([
    { value: 'academic', label: 'Academic' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'behavioural', label: 'Behavioural' },
    { value: 'custodial', label: 'Custodial' },
    { value: 'health', label: 'Health' },
    { value: 'personal', label: 'Personal' },
  ]);

  @computed('reason.value')
  get needsPostSchoolActivity() {
    switch (this.get('reason.value')) {
      case 'O':
      case 'X':
      case 'L':
      case 'E':
      case 'J':
        return true;
    }
    return false;
  }

  @computed('reason.value')
  get needsNextSchoolFields() {
    switch (this.get('reason.value')) {
      case 'S':
      case 'K':
      case 'C':
      case 'P':
        return true;
    }
    return false;
  }

  get isPending() {
    let endDate = moment(this.endDate);
    let now = moment();
    return endDate.isAfter(now, 'd');
  }

  @computed(
    'jurisdictionalIdIsValid',
    'person.{gender,dob,currentYearLevel}',
    'enrolment',
  )
  get uploadValidationErrors() {
    return validateLeaver(this.person, this.enrolment, this.schoolType);
  }

  @computed('person.jursidictionalId')
  get jurisdictionalIdIsValid() {
    return jurisdictionalIdValidator(this.person.jurisdictionalId);
  }

  didReceiveAttrs() {
    this.endDate = this.enrolment.exitDate;
    if (isEmpty(this.enrolment.meta)) {
      this.enrolment.set('meta', {});
    }

    let reason = this.enrolment.departureReason;
    let destinationSchool = this.enrolment.meta.get(
      'fields.nzMOE:destinationSchool.value',
    );
    let destinationArea = this.enrolment.meta.get(
      'fields.nzMOE:destinationArea.value',
    );
    let postSchoolActivity = this.enrolment.meta.get(
      'fields.nzMOE:postSchoolActivity.value',
    );

    this.reason = findBy(DepartureReason.values, 'value', reason);
    this.destinationSchool = findBy(School.values, 'value', destinationSchool);
    this.destinationArea = findBy(Area.values, 'value', destinationArea);
    this.postSchoolActivity = findBy(
      PostSchoolActivity.values,
      'value',
      postSchoolActivity,
    );
    this.learnerFlags =
      this.enrolment.meta.get('fields.nzMOE:learnerFlags.value') ?? [];

    return super.didReceiveAttrs(...arguments);
  }

  @tracked
  unenrolling = false;

  @tracked
  postSchoolActivity;

  @action
  setPostSchoolActivity(value) {
    this.postSchoolActivity = value;
  }

  @action
  toggleFlag(value) {
    if (this.learnerFlags.includes(value)) {
      this.learnerFlags = this.learnerFlags.filter((flag) => flag !== value);
    } else {
      this.learnerFlags = [...this.learnerFlags, value];
    }
  }

  @action
  unenrol() {
    this.unenrolling = true;
    // Update the enrolment record before saving to ensure that we don't accidentally wipe anything
    // which was updated by the Person update in the previous step.
    this.enrolment.reload().then((enrolment) => {
      let endDate = moment(this.endDate);

      // Initialise dynamic fields
      if (isEmpty(this.enrolment.meta)) {
        this.enrolment.set('meta', {});
      }

      // Create/update dynamic field values
      let areaField = enrolment.meta.get('fields.nzMOE:destinationArea');
      if (isEmpty(areaField)) {
        enrolment.meta.addField('nzMOE:destinationArea', DataType.STRING);
        areaField = enrolment.meta.get('fields.nzMOE:destinationArea');
      }

      let schoolField = enrolment.meta.get('fields.nzMOE:destinationSchool');
      if (isEmpty(schoolField)) {
        enrolment.meta.addField('nzMOE:destinationSchool', DataType.STRING);
        schoolField = enrolment.meta.get('fields.nzMOE:destinationSchool');
      }

      let flagsField = enrolment.meta.get('fields.nzMOE:learnerFlags');
      if (isEmpty(flagsField)) {
        enrolment.meta.addField('nzMOE:learnerFlags', DataType.LIST);
        flagsField = enrolment.meta.get('fields.nzMOE:learnerFlags');
      }

      let activityField = enrolment.meta.get('fields.nzMOE:postSchoolActivity');
      if (isEmpty(activityField)) {
        enrolment.meta.addField('nzMOE:postSchoolActivity', DataType.STRING);
        activityField = enrolment.meta.get('fields.nzMOE:postSchoolActivity');
      }

      if (this.needsNextSchoolFields) {
        areaField.set('value', this.get('destinationArea.value'));
        schoolField.set('value', this.get('destinationSchool.value'));
        flagsField.set('value', [...this.learnerFlags]);
      } else {
        areaField.set('value', '');
        schoolField.set('value', '');
        flagsField.set('value', []);
      }

      if (this.needsPostSchoolActivity) {
        activityField.set('value', this.get('postSchoolActivity.value'));
      } else {
        activityField.set('value', '');
      }

      // Set core enrolment fields
      enrolment.setProperties({
        exitDate: endDate.format('YYYY-MM-DD'),
        departureReason: this.reason.value,
      });

      // Update enrolment status, if they've actually left
      if (!this.isPending && !this.editing) {
        enrolment.set('status', EnrolmentStatus.FORMER);
      }

      enrolment.save().then(() => {
        this.unenrolling = false;
        this.step = this.step + 1;
      });
    });
  }
}
